import { CommandContribution, CommandRegistry, MenuContribution, MenuModelRegistry } from '@theia/core';
import { inject, injectable } from '@theia/core/shared/inversify';
import { NavigatorContextMenu } from '@theia/navigator/lib/browser/navigator-contribution';
import { WorkspaceService } from '@theia/workspace/lib/browser/workspace-service';
import {
    TEMPLATE_CREATE_COMMAND_BY_ROOT,
    TEMPLATE_ROOTS,
    TemplateCreateCommands,
    TemplateRoot
} from './template-create-commands';
import { TemplateCreateService } from './template-create-service';

const CONTEXT_MENU_ORDER: Record<TemplateRoot, string> = {
    screens: 'lvgl-a',
    components: 'lvgl-b',
    widgets: 'lvgl-c',
    tests: 'lvgl-d'
};

@injectable()
export class TemplateCreateContribution implements CommandContribution, MenuContribution {
    @inject(TemplateCreateService)
    protected readonly templateCreateService!: TemplateCreateService;

    @inject(WorkspaceService)
    protected readonly workspaceService!: WorkspaceService;

    registerCommands(registry: CommandRegistry): void {
        for (const templateRoot of TEMPLATE_ROOTS) {
            const command = TEMPLATE_CREATE_COMMAND_BY_ROOT[templateRoot];
            registry.registerCommand(command, {
                execute: () => this.templateCreateService.create(templateRoot),
                isEnabled: () => this.canRun(templateRoot),
                isVisible: () => this.canRun(templateRoot)
            });
        }
    }

    registerMenus(registry: MenuModelRegistry): void {
        registry.registerMenuAction(NavigatorContextMenu.MODIFICATION, {
            commandId: TemplateCreateCommands.NEW_SCREEN.id,
            order: CONTEXT_MENU_ORDER.screens
        });
        registry.registerMenuAction(NavigatorContextMenu.MODIFICATION, {
            commandId: TemplateCreateCommands.NEW_COMPONENT.id,
            order: CONTEXT_MENU_ORDER.components
        });
        registry.registerMenuAction(NavigatorContextMenu.MODIFICATION, {
            commandId: TemplateCreateCommands.NEW_WIDGET.id,
            order: CONTEXT_MENU_ORDER.widgets
        });
        registry.registerMenuAction(NavigatorContextMenu.MODIFICATION, {
            commandId: TemplateCreateCommands.NEW_TEST.id,
            order: CONTEXT_MENU_ORDER.tests
        });
    }

    protected canRun(templateRoot: TemplateRoot): boolean {
        if (!this.workspaceService.opened) {
            return false;
        }

        const selectedRoot = this.templateCreateService.getTemplateRootForSelection();
        if (selectedRoot) {
            return selectedRoot === templateRoot && this.templateCreateService.canCreate(templateRoot);
        }

        return this.templateCreateService.canCreate(templateRoot);
    }
}
